/**
 * 무역통계부호 조회 (?page=trade-codes) — 공개 도구. 문의(김은정) 대응.
 *   신고 부호: 부호군(인도조건·징수형태·세관·수출입 거래구분/종류 등 77군) 선택 + 검색.
 *   장치장 부호: 보세구역(장치장) 부호·명칭·관할세관·주소 검색.
 * 데이터: /api/trade-codes/* (LCS_V2 마스터 정적 export — 개정 시 export 재실행).
 */
import React, { useEffect, useMemo, useState } from "react";
import ApiPortalLayout from "./ApiPortalLayout";

const S = {
  page: { maxWidth: 880, color: "#1f2937",
    fontFamily: "'Inter','Pretendard',sans-serif", lineHeight: 1.65 },
  h1: { fontSize: 30, fontWeight: 800, marginBottom: 6 },
  p: { fontSize: 14, color: "#4b5563", margin: "6px 0" },
  card: { border: "1px solid #e5e7eb", borderRadius: 12, padding: "18px 20px", marginTop: 16 },
  label: { fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 },
  input: { width: "100%", height: 44, padding: "0 12px", fontSize: 14, borderRadius: 8,
    border: "1px solid #d1d5db", outline: "none", background: "#fff",
    fontFamily: "inherit", boxSizing: "border-box" },
  note: { background: "#f8fafc", borderLeft: "3px solid #94a3b8", padding: "10px 14px",
    fontSize: 12.5, color: "#475569", borderRadius: 4, margin: "14px 0 0" },
  th: { textAlign: "left", padding: "8px 10px", whiteSpace: "nowrap", fontSize: 12,
    fontWeight: 700, color: "#374151", position: "sticky", top: 0, zIndex: 2,
    background: "#f8fafc", boxShadow: "inset 0 -2px 0 #e5e7eb" },
  td: { textAlign: "left", padding: "6px 10px", borderBottom: "1px solid #f3f4f6",
    fontSize: 13, background: "#fff", verticalAlign: "top" },
  code: { fontFamily: "ui-monospace,monospace", fontWeight: 700, whiteSpace: "nowrap" },
  tab: (on) => ({ height: 38, padding: "0 16px", borderRadius: 8, cursor: "pointer",
    border: on ? "1px solid #111827" : "1px solid #d1d5db",
    background: on ? "#111827" : "#fff", color: on ? "#fff" : "#4b5563",
    fontSize: 13.5, fontWeight: 700, fontFamily: "inherit" }),
  chip: (on) => ({ padding: "5px 11px", borderRadius: 999, cursor: "pointer",
    border: on ? "1px solid #111827" : "1px solid #d1d5db",
    background: on ? "#111827" : "#fff", color: on ? "#fff" : "#4b5563",
    fontSize: 12.5, fontWeight: 600, fontFamily: "inherit", whiteSpace: "nowrap" }),
};

// 문의로 요청된 부호군을 상단 바로가기로 — 그 외는 전체 드롭다운.
const QUICK_CLASSES = [
  ["A0039", "징수형태"],
  ["A0005", "인도조건"],
  ["A0006", "세관부호"],
  ["A0007", "세관과부호"],
  ["A0018", "수출거래구분"],
  ["A0042", "수입거래구분"],
  ["A0019", "수출종류"],
  ["A0043", "수입종류"],
];

export default function TradeCodesPage({ onNavigate, embedded = false }) {
  const [tab, setTab] = useState("codes"); // codes | warehouses
  const [classes, setClasses] = useState(null);
  const [cls, setCls] = useState("A0039");
  const [codes, setCodes] = useState(null);
  const [q, setQ] = useState("");
  const [whQ, setWhQ] = useState("");
  const [wh, setWh] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    fetch("/api/trade-codes/classes")
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => setClasses(d.classes || []))
      .catch((e) => setErr(String(e.message || e)));
  }, []);

  useEffect(() => {
    if (tab !== "codes" || !cls) return;
    setCodes(null);
    fetch(`/api/trade-codes/codes?cls=${encodeURIComponent(cls)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(setCodes)
      .catch((e) => setErr(String(e.message || e)));
  }, [tab, cls]);

  useEffect(() => {
    if (tab !== "warehouses") return;
    const t = setTimeout(() => {
      fetch(`/api/trade-codes/warehouses?q=${encodeURIComponent(whQ)}&limit=200`)
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
        .then(setWh)
        .catch((e) => setErr(String(e.message || e)));
    }, 250); // 타이핑 debounce
    return () => clearTimeout(t);
  }, [tab, whQ]);

  const filtered = useMemo(() => {
    if (!codes?.items) return [];
    const needle = q.toLowerCase().trim();
    if (!needle) return codes.items;
    return codes.items.filter(
      (c) =>
        c.cd.toLowerCase().includes(needle) ||
        (c.nm || "").toLowerCase().includes(needle) ||
        (c.shrt_nm || "").toLowerCase().includes(needle),
    );
  }, [codes, q]);

  const clsMeta = classes?.find((c) => c.cls_cd === cls);

  return (
    <ApiPortalLayout active="trade-codes" onNavigate={onNavigate} embedded={embedded}>
      <div style={S.page}>
        <h1 style={S.h1}>무역통계부호</h1>
        <p style={S.p}>
          수출입 신고에 쓰는 <b>무역통계부호</b>를 조회합니다 — 징수형태 · 인도조건 ·
          세관부호(관할구역) · 수출입 거래구분/종류 등 77개 부호군과 장치장(보세구역)
          부호.
        </p>

        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <button style={S.tab(tab === "codes")} onClick={() => setTab("codes")}>
            신고 부호
          </button>
          <button style={S.tab(tab === "warehouses")} onClick={() => setTab("warehouses")}>
            장치장(보세구역) 부호
          </button>
        </div>

        {err && (
          <div style={{ ...S.note, borderLeftColor: "#dc2626", color: "#b91c1c" }}>
            불러오기 오류: {err}
          </div>
        )}

        {tab === "codes" && (
          <div style={S.card}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
              {QUICK_CLASSES.map(([cd, label]) => (
                <button key={cd} style={S.chip(cls === cd)} onClick={() => { setCls(cd); setQ(""); }}>
                  {label}
                </button>
              ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "minmax(220px, 320px) 1fr",
              gap: 12, alignItems: "end" }}>
              <div>
                <label style={S.label}>부호군 (전체 {classes ? classes.length : "…"}종)</label>
                <select value={cls} onChange={(e) => { setCls(e.target.value); setQ(""); }} style={S.input}>
                  {(classes || []).map((c) => (
                    <option key={c.cls_cd} value={c.cls_cd}>
                      {c.cls_nm} ({c.n})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={S.label}>부호·명칭 검색</label>
                <input value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="예: 11 / 신용장 / FOB" style={S.input} />
              </div>
            </div>

            <div style={{ maxHeight: 520, overflowY: "auto", marginTop: 14,
              border: "1px solid #f3f4f6", borderRadius: 8 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ ...S.th, width: 90 }}>부호</th>
                    <th style={S.th}>명칭</th>
                    <th style={{ ...S.th, width: 140 }}>약어</th>
                  </tr>
                </thead>
                <tbody>
                  {!codes && (
                    <tr><td style={S.td} colSpan={3}>불러오는 중…</td></tr>
                  )}
                  {codes && filtered.length === 0 && (
                    <tr><td style={S.td} colSpan={3}>결과 없음</td></tr>
                  )}
                  {filtered.map((c) => (
                    <tr key={`${c.cd}`}>
                      <td style={{ ...S.td, ...S.code }}>{c.cd}</td>
                      <td style={S.td}>{c.nm}</td>
                      <td style={{ ...S.td, color: "#6b7280" }}>{c.shrt_nm || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {clsMeta?.rmk && (
              <div style={{ fontSize: 12.5, color: "#6b7280", marginTop: 8 }}>
                비고: {clsMeta.rmk}
              </div>
            )}
          </div>
        )}

        {tab === "warehouses" && (
          <div style={S.card}>
            <label style={S.label}>장치장 부호·명칭·관할세관·주소 검색</label>
            <input value={whQ} onChange={(e) => setWhQ(e.target.value)}
              placeholder="예: 인천세관 / 02006119 / 보세창고명" style={S.input} />
            <div style={{ fontSize: 12.5, color: "#6b7280", margin: "8px 0 0" }}>
              {wh ? `${wh.total.toLocaleString()}건${wh.total > wh.items.length ? ` 중 ${wh.items.length}건 표시 — 검색어로 좁혀주세요` : ""}` : "불러오는 중…"}
            </div>
            <div style={{ maxHeight: 520, overflowY: "auto", marginTop: 10,
              border: "1px solid #f3f4f6", borderRadius: 8 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ ...S.th, width: 100 }}>부호</th>
                    <th style={S.th}>명칭</th>
                    <th style={{ ...S.th, width: 110 }}>관할세관</th>
                    <th style={S.th}>주소</th>
                  </tr>
                </thead>
                <tbody>
                  {(wh?.items || []).map((w) => (
                    <tr key={w.cd}>
                      <td style={{ ...S.td, ...S.code }}>{w.cd}</td>
                      <td style={S.td}>{w.nm}</td>
                      <td style={{ ...S.td, whiteSpace: "nowrap" }}>{w.customs || ""}</td>
                      <td style={{ ...S.td, color: "#6b7280" }}>{w.addr || ""}</td>
                    </tr>
                  ))}
                  {wh && wh.items.length === 0 && (
                    <tr><td style={S.td} colSpan={4}>결과 없음</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div style={S.note}>
          참고용 부호입니다 — 신고 시점의 관세청 고시(무역통계부호집) 개정 여부를
          확인하세요. 부호 개정·누락 제보는 문의·건의로 남겨주시면 반영합니다.
        </div>
      </div>
    </ApiPortalLayout>
  );
}
